import type { DeviceSettings } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  InfoButton,
  Label,
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
  useSortedSliderValueInputProps,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react"

import styles from "./Display.module.scss"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import {
  useSliderInputProps,
  useGetGlucoseValue,
  useSetGlucoseValue,
  useGlucoseInputProps,
  useReadableGlucoseUnit,
} from "src/hooks"
import { clamp } from "src/utils"

const polyglotPrefix = "pages.settings.display.graph_range."

const inputProps = {
  min: 0,
  max: 400,
  mgdlStep: 10,
}

interface Props {
  updatedAt?: string;
  updateDeviceSettings: (settings: Partial<DeviceSettings>) => void;
}

export function GraphRange(props: Props) {
  const {
    updateDeviceSettings,
    updatedAt,
  } = props

  const polyglot = usePolyglot()
  const { currentDeviceSettings } = useFollowedUsers()
  const currentChartBottom = currentDeviceSettings.chart_bottom
  const currentChartTop = currentDeviceSettings.chart_top
  const editedAtRef = useRef<string>(dayjs().toISOString())

  const readableGluecoseUnit = useReadableGlucoseUnit()
  const getGlucoseValue = useGetGlucoseValue()
  const setGlucoseValue = useSetGlucoseValue()

  const [
    valueOne,
    setValueOne,
  ] = useState<number>(currentChartBottom)

  const [
    valueTwo,
    setValueTwo,
  ] = useState<number>(currentChartTop)

  // when the settings property changes, update the state
  useEffect(
    () => {
      if (!updatedAt || dayjs(updatedAt).isAfter(editedAtRef.current)) {
        setValueOne(currentChartBottom)
        setValueTwo(currentChartTop)
      }
    },
    [
      editedAtRef,
      currentChartBottom,
      currentChartTop,
      updatedAt,
      setValueOne,
      setValueTwo,
    ],
  )

  // when value one changes, update the state and trigger a settings update
  const onValueOneChange = useCallback(
    (v: number) => {
      const newValue = clamp(
        setGlucoseValue(v),
        inputProps.min,
        inputProps.max,
      )
      setValueOne(newValue)

      const chartBottom = Math.min(
        newValue,
        valueTwo,
      )
      const chartTop = Math.max(
        newValue,
        valueTwo,
      )

      updateDeviceSettings({
        chart_bottom: chartBottom,
        chart_top: chartTop,
      })

      editedAtRef.current = dayjs().toISOString()
    },
    [
      editedAtRef,
      setValueOne,
      valueTwo,
      updateDeviceSettings,
      setGlucoseValue,
    ],
  )

  // when value two changes, update the state and trigger a settings update
  const onValueTwoChange = useCallback(
    (v: number) => {
      const newValue = clamp(
        setGlucoseValue(v),
        inputProps.min,
        inputProps.max,
      )
      setValueTwo(newValue)

      const chartBottom = Math.min(
        newValue,
        valueOne,
      )
      const chartTop = Math.max(
        newValue,
        valueOne,
      )

      updateDeviceSettings({
        chart_bottom: chartBottom,
        chart_top: chartTop,
      })

      editedAtRef.current = dayjs().toISOString()
    },
    [
      setGlucoseValue,
      editedAtRef,
      setValueTwo,
      valueOne,
      updateDeviceSettings,
    ],
  )

  // shared props for all inputs
  const sharedProps = useGlucoseInputProps(inputProps)

  // the unique props for the first slider input
  const inputOneProps = useSliderInputProps({
    value: getGlucoseValue(valueOne),
    onChange: onValueOneChange,
    updatedAt,
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
    toFixed: true,
  })

  // the unique props for the second slider input
  const inputTwoProps = useSliderInputProps({
    value: getGlucoseValue(valueTwo),
    onChange: onValueTwoChange,
    updatedAt,
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
    toFixed: true,
  })

  const numberInputOneProps = useMemo(
    () => ({
      value: inputOneProps.inputValue,
      onChange: inputOneProps.onNumberChange,
      onBlur: inputOneProps.onNumberBlur,
      onKeyDown: inputOneProps.onNumberKeyDown,
    }),
    [inputOneProps],
  )

  const numberInputTwoProps = useMemo(
    () => ({
      value: inputTwoProps.inputValue,
      onChange: inputTwoProps.onNumberChange,
      onBlur: inputTwoProps.onNumberBlur,
      onKeyDown: inputTwoProps.onNumberKeyDown,
    }),
    [inputTwoProps],
  )

  const [
    lowInputProps,
    highInputProps,
  ] = useSortedSliderValueInputProps(
    numberInputOneProps,
    numberInputTwoProps,
  )

  return (
    <div>
      <Label id="graph-range-label">
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}title`)}>
          <div>
            {polyglot.t(`${polyglotPrefix}description_one`)}
          </div>
          <div>
            {polyglot.t(`${polyglotPrefix}description_two`)}
          </div>
        </InfoButton>
      </Label>
      <div className={styles.inputContainer}>
        <SliderValue
          sizePreset="small"
          alignmentPreset="center"
        >
          <SliderValueInput
            {...sharedProps}
            {...lowInputProps}
            placeholder=""
            id="range-slider-min-val"
          />
          <SliderValueLabelContainer>
            <SliderValueLabel id="slider-units">
              {readableGluecoseUnit}
            </SliderValueLabel>
          </SliderValueLabelContainer>
        </SliderValue>
        <Slider
          className={styles.rangeInputSlider}
          id="graph-range-slider"
        >
          <SliderInput
            value={inputOneProps.sliderValue}
            onChange={inputOneProps.onSliderChange}
            {...sharedProps}
          />
          <SliderInput
            value={inputTwoProps.sliderValue}
            onChange={inputTwoProps.onSliderChange}
            {...sharedProps}
          />
          <SliderTracks
            min={sharedProps.min}
            max={sharedProps.max}
          />
        </Slider>
        <SliderValue
          sizePreset="small"
          alignmentPreset="center"
        >
          <SliderValueInput
            {...sharedProps}
            {...highInputProps}
            placeholder=""
            id="range-slider-max-val"
          />
          <SliderValueLabelContainer>
            <SliderValueLabel>
              {readableGluecoseUnit}
            </SliderValueLabel>
          </SliderValueLabelContainer>
        </SliderValue>
      </div>
    </div>
  )
}
